// Presentation-only analysis of device data. Nothing here is persisted or uploaded.
export const WORKOUT_PULSE_ZONES = [
  { zone: 1, label: "Sehr leicht", color: "#80b5cf", lower: 0, upper: .6 },
  { zone: 2, label: "Locker", color: "#a9c7b5", lower: .6, upper: .7 },
  { zone: 3, label: "Moderat", color: "#d6c38c", lower: .7, upper: .8 },
  { zone: 4, label: "Intensiv", color: "#dc9d7a", lower: .8, upper: .9 },
  { zone: 5, label: "Sehr intensiv", color: "#dc8896", lower: .9, upper: Infinity },
]
const bpmValue = value => typeof value === "number" && Number.isFinite(value) && value >= 20 && value <= 300
export function validPulseMaximum(value) { return typeof value === "number" && Number.isInteger(value) && value >= 100 && value <= 240 }
export function workoutPulseZone(bpm, maximum) {
  if (!bpmValue(bpm) || !validPulseMaximum(maximum)) return null
  return WORKOUT_PULSE_ZONES.find(zone => bpm < Math.ceil(zone.upper * maximum))?.zone || 5
}
export function estimatedPulseMaximum(birthday, at = Date.now()) {
  // Existing profile date only. No assumed age, inferred fitness or observed peak.
  if (typeof birthday !== "string" || !/^\d{4}-\d{2}-\d{2}(?:T.*)?$/.test(birthday)) return null
  const [year, month, day] = birthday.slice(0, 10).split("-").map(Number), birth = new Date(Date.UTC(year, month - 1, day)), now = new Date(at)
  if (birth.getUTCFullYear() !== year || birth.getUTCMonth() !== month - 1 || birth.getUTCDate() !== day || !Number.isFinite(now.getTime())) return null
  const age = now.getFullYear() - year - (now.getMonth() + 1 < month || (now.getMonth() + 1 === month && now.getDate() < day) ? 1 : 0)
  return age >= 18 && age <= 100 ? 220 - age : null
}
export function pulseTime(seconds) {
  const total = Math.max(0, Math.round(Number(seconds) || 0)), hours = Math.floor(total / 3600), minutes = Math.floor(total % 3600 / 60)
  return hours ? `${hours}:${String(minutes).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}` : `${minutes}:${String(total % 60).padStart(2, "0")}`
}

export function analyzeWorkoutPulse(recording, maximum = null) {
  const elapsed = (Date.parse(recording?.endedAt) - Date.parse(recording?.startedAt)) / 1000
  const duration = Number.isFinite(elapsed) && elapsed > 0 && elapsed <= 7 * 86400 ? elapsed : 0
  const points = new Map(), conflicts = new Set()
  for (const point of Array.isArray(recording?.heartRateSamples) ? recording.heartRateSamples.slice(0, 2400) : []) {
    if (!duration || !Number.isFinite(point?.offsetSeconds) || point.offsetSeconds < 0 || point.offsetSeconds > duration || !bpmValue(point.bpm)) continue
    const previous = points.get(point.offsetSeconds)
    if (previous && previous.bpm !== point.bpm) conflicts.add(point.offsetSeconds)
    points.set(point.offsetSeconds, { offsetSeconds: point.offsetSeconds, bpm: point.bpm, segment: Number.isInteger(point.segment) && point.segment >= 0 ? point.segment : null })
  }
  const samples = [...points.values()].filter(point => !conflicts.has(point.offsetSeconds)).sort((a, b) => a.offsetSeconds - b.offsetSeconds)
  const raw = recording?.heartRateAnalysis
  let histogram = [], native = false, observed = 0
  if (duration && raw?.version === 1 && raw.maxGapSeconds === 30 && Number.isInteger(raw.sampleCount) && raw.sampleCount > 0 && raw.sampleCount <= 20000 &&
      typeof raw.truncated === "boolean" && Array.isArray(raw.histogram) && raw.histogram.length <= 281 &&
      Number.isFinite(raw.observedSeconds) && raw.observedSeconds >= 0 && raw.observedSeconds <= duration + .02) {
    const seen = new Set()
    const valid = raw.histogram.every(item => {
      if (!Number.isInteger(item?.bpm) || !bpmValue(item.bpm) || seen.has(item.bpm) || !Number.isFinite(item.seconds) || item.seconds <= 0) return false
      seen.add(item.bpm); return true
    })
    const sum = raw.histogram.reduce((value, item) => value + (Number.isFinite(item?.seconds) ? item.seconds : 0), 0)
    if (valid && Math.abs(sum - raw.observedSeconds) < .02 && sum <= duration + .02) {
      observed = Math.min(sum, duration)
      histogram = raw.histogram.map(item => ({ ...item, seconds: sum > duration ? item.seconds * duration / sum : item.seconds }))
      native = true
    }
  }
  if (!native) {
    const bins = new Map()
    // Older APKs expose a thinned curve. Use conservative interval estimates;
    // never let sparse points or missing tails stand in for the full session.
    for (let index = 1; index < samples.length; index++) {
      const a = samples[index - 1], b = samples[index], delta = b.offsetSeconds - a.offsetSeconds
      if (delta > 90 || a.segment !== b.segment || [...conflicts].some(time => time > a.offsetSeconds && time < b.offsetSeconds)) continue
      bins.set(a.bpm, (bins.get(a.bpm) || 0) + delta / 2); bins.set(b.bpm, (bins.get(b.bpm) || 0) + delta / 2)
      observed += delta
    }
    histogram = [...bins].map(([bpm, seconds]) => ({ bpm, seconds }))
  }
  const zones = WORKOUT_PULSE_ZONES.map(zone => {
    const seconds = validPulseMaximum(maximum) ? histogram.filter(item => workoutPulseZone(item.bpm, maximum) === zone.zone).reduce((sum, item) => sum + item.seconds, 0) : 0
    const low = Math.ceil(zone.lower * maximum), high = Math.ceil(zone.upper * maximum) - 1
    return { ...zone, seconds, percent: observed > 0 ? seconds / observed * 100 : 0,
      range: !validPulseMaximum(maximum) ? "" : zone.zone === 1 ? `< ${high + 1} BPM` : zone.zone === 5 ? `ab ${low} BPM` : `${low}–${high} BPM`,
      percentLabel: zone.zone === 1 ? "unter 60 %" : zone.zone === 5 ? "ab 90 %" : `${zone.lower * 100}–${zone.upper * 100} %` }
  })
  const values = samples.map(point => point.bpm)
  const minimum = bpmValue(recording?.minimumHeartRate) ? recording.minimumHeartRate : values.length ? Math.min(...values) : null
  const peak = bpmValue(recording?.maximumHeartRate) ? recording.maximumHeartRate : values.length ? Math.max(...values) : null
  const average = observed > 0 ? histogram.reduce((sum, item) => sum + item.bpm * item.seconds, 0) / observed : bpmValue(recording?.averageHeartRate) ? recording.averageHeartRate : values.length ? values.reduce((a,b) => a+b, 0) / values.length : null
  return { duration, samples, zones, observed, native, approximate: !native, average, minimum, peak,
    coverage: duration ? observed / duration * 100 : 0, missing: Math.max(0, duration - observed),
    sampleCount: native ? raw.sampleCount : samples.length, truncated: native ? raw.truncated : recording?.heartRateStatus === "limited",
    dominant: validPulseMaximum(maximum) && observed > 0 ? [...zones].sort((a,b) => b.seconds - a.seconds)[0] : null }
}

export function workoutPulseChart(analysis, maximum = null) {
  const samples = analysis.samples || [], values = samples.map(point => point.bpm)
  if (!values.length) return { dots: [], paths: [], bands: [], ticks: [] }
  const low = Math.max(20, Math.floor((Math.min(...values) - 12) / 10) * 10), high = Math.min(300, Math.ceil((Math.max(...values) + 12) / 10) * 10)
  const y = bpm => 150 - (bpm - low) / (high - low) * 128
  const dots = samples.map(point => ({ ...point, x: 8 + point.offsetSeconds / Math.max(1, analysis.duration) * 284, y: y(point.bpm), zone: workoutPulseZone(point.bpm, maximum) }))
  const paths = []; let line = []
  const flush = () => { if (line.length > 1) paths.push({ points: line.map(p => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(" "), zone: line[0].zone }); line = [] }
  dots.forEach((point, index) => {
    const previous = dots[index - 1]
    if (previous && (point.segment !== previous.segment || point.offsetSeconds - previous.offsetSeconds > (analysis.native && point.segment !== null && previous.segment !== null ? Infinity : 90))) flush()
    else if (previous && point.zone !== previous.zone) { line.push(point); flush() }
    line.push(point)
  }); flush()
  const bands = validPulseMaximum(maximum) ? WORKOUT_PULSE_ZONES.map(zone => {
    const from = Math.max(low, zone.lower * maximum), to = Math.min(high, zone.upper * maximum)
    return { ...zone, y: y(to), height: to > from ? y(from) - y(to) : 0 }
  }).filter(band => band.height > 0) : []
  return { dots, paths, bands, ticks: [low, Math.round((high + low) / 2), high].map(value => ({ value, y: y(value) })) }
}
